import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { toast } from '../../../../shared/useToast.js';
import { api, apiError } from '../../../../shared/api.js';
import { useRoles } from '../../hooks/useRoles.js';
import {
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
  useUpdateRolePermissions
} from '../../hooks/useRoleMutations.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import { PermissionMatrix } from './PermissionMatrix.jsx';
import { RoleForm } from './RoleForm.jsx';
import { ConfirmModal } from '../shared/ConfirmModal.jsx';

function isWildcard(role) {
  if (!role) return false;
  const code = role.code || role.key || role.name;
  if (code === 'super_admin' || role.isSuperAdmin) return true;
  if (Array.isArray(role.permissions) && role.permissions.includes('*')) return true;
  return false;
}

export function RolesPanel() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canEdit = hasPermission('settings.roles.edit');

  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  const { data: permGroups = [], isLoading: permsLoading } = usePermissions();
  const updatePerms = useUpdateRolePermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();

  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDefaults, setConfirmDefaults] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    if (!selectedId && roles.length) {
      setSelectedId(roles[0]._id || roles[0].id);
    }
  }, [roles, selectedId]);

  const selected = useMemo(
    () => roles.find((r) => (r._id || r.id) === selectedId) || null,
    [roles, selectedId]
  );
  const wildcard = isWildcard(selected);

  useEffect(() => {
    if (selected) {
      setDraft(Array.isArray(selected.permissions) ? [...selected.permissions] : []);
      setDirty(false);
    }
  }, [selected]);

  function togglePerm(key) {
    if (wildcard || !canEdit) return;
    setDraft((cur) => {
      const arr = Array.isArray(cur) ? cur : [];
      const next = arr.includes(key) ? arr.filter((p) => p !== key) : [...arr, key];
      setDirty(true);
      return next;
    });
  }

  async function savePerms() {
    if (!selected || wildcard) return;
    await updatePerms.mutateAsync(
      { id: selected._id || selected.id, permissions: draft },
      { onSuccess: () => setDirty(false) }
    );
  }

  function openCreateRole() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEditRole(r) {
    setEditing(r);
    setFormOpen(true);
  }

  async function handleSubmitRole(body) {
    if (editing) {
      await updateRole.mutateAsync(
        { id: editing._id || editing.id, body },
        { onSuccess: () => setFormOpen(false) }
      );
    } else {
      await createRole.mutateAsync(body, {
        onSuccess: (created) => {
          setFormOpen(false);
          const id = created?._id || created?.id || created?.role?._id;
          if (id) setSelectedId(id);
        }
      });
    }
  }

  if (rolesLoading || permsLoading) {
    return (
      <Card>
        <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
      </Card>
    );
  }

  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: '280px 1fr' }}>
      <div className="card">
        <div className="card-h">
          <h3 className="text-[14px] font-bold flex-1">الأدوار</h3>
          {canEdit ? (
            <button
              type="button"
              className="btn"
              style={{ padding: '4px 8px' }}
              onClick={openCreateRole}
              title="دور جديد"
            >
              <Icon name="plus" size={13} />
            </button>
          ) : null}
        </div>
        <div className="card-b space-y-1">
          {roles.map((r) => {
            const id = r._id || r.id;
            const active = id === selectedId;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setSelectedId(id)}
                className="w-full text-start p-2.5 rounded-[10px] flex items-center gap-2.5 transition-colors"
                style={{
                  border: active ? `1px solid ${r.color || '#FF7A1A'}` : '1px solid transparent',
                  background: active ? `${r.color || '#FF7A1A'}12` : 'transparent'
                }}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-none"
                  style={{ background: r.color || '#6B7280' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[13px] flex items-center gap-1.5">
                    {r.name}
                    {r.isSystem ? (
                      <span title="دور افتراضي" className="text-[10px] text-ink-3">🔒</span>
                    ) : null}
                  </div>
                  <div className="text-[10.5px] text-ink-3 truncate">{r.description || '—'}</div>
                </div>
                <Pill variant="muted">{r.userCount ?? r.usersCount ?? 0}</Pill>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-h flex-wrap gap-2">
          <h3 className="text-[14px] font-bold flex-1">
            صلاحيات: {selected?.name || '—'}
          </h3>
          <span className="text-[11px] text-ink-3">
            {wildcard ? 'كل الصلاحيات' : `${draft.length} صلاحية`}
          </span>
          {selected && canEdit && !selected.isSystem ? (
            <button
              type="button"
              className="btn"
              style={{ fontSize: 12 }}
              onClick={() => openEditRole(selected)}
            >
              <Icon name="edit" size={12} /> تعديل
            </button>
          ) : null}
          {selected && canEdit && !selected.isSystem ? (
            <button
              type="button"
              className="btn btn-danger"
              style={{ fontSize: 12 }}
              onClick={() => setConfirmDelete(selected)}
            >
              <Icon name="trash" size={12} />
            </button>
          ) : null}
          {selected && canEdit && !wildcard && selected.isSystem ? (
            <button
              type="button"
              className="btn"
              style={{ fontSize: 12 }}
              onClick={() => setConfirmDefaults(true)}
              disabled={loadingDefaults}
              title="إرجاع صلاحيات هذا الدور كما عرّفها النظام"
            >
              ↻ الصلاحيات الافتراضية
            </button>
          ) : null}
          {canEdit && !wildcard ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: 12 }}
              onClick={savePerms}
              disabled={!dirty || updatePerms.isPending}
            >
              <Icon name="check" size={12} /> {updatePerms.isPending ? 'جاري...' : 'حفظ'}
            </button>
          ) : null}
        </div>

        <div className="card-b">
          {!selected ? (
            <div className="text-ink-3 text-center py-10 text-sm">اختر دوراً من اليمين</div>
          ) : wildcard ? (
            <div className="text-center py-10 text-ink-2 text-sm space-y-2">
              <div className="text-2xl">🛡️</div>
              <div className="font-bold text-[14px]">Super Admin</div>
              <div className="text-ink-3 text-[12.5px]">
                يمتلك جميع الصلاحيات تلقائياً — لا يمكن التعديل.
              </div>
            </div>
          ) : (
            <PermissionMatrix
              groups={permGroups}
              selected={draft}
              onToggle={togglePerm}
              disabled={!canEdit}
            />
          )}
        </div>
      </div>

      <RoleForm
        open={formOpen}
        role={editing}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmitRole}
        submitting={createRole.isPending || updateRole.isPending}
      />

      <ConfirmModal
        open={confirmDefaults}
        title="الصلاحيات الافتراضية"
        message="هل أنت متأكد من إعادة الصلاحيات إلى الإعدادات الافتراضية؟ لن تُحفَظ التغييرات حتى تضغط «حفظ»."
        confirmLabel="إعادة"
        loading={loadingDefaults}
        onClose={() => setConfirmDefaults(false)}
        onConfirm={async () => {
          if (!selected) return;
          setLoadingDefaults(true);
          try {
            const { data } = await api.get(`/roles/${selected._id || selected.id}/defaults`);
            const perms = Array.isArray(data?.permissions) ? data.permissions : [];
            // تُحدَّث القائمة في الصفحة فوراً، والحفظ الفعلي عند الضغط على «حفظ».
            setDraft(perms);
            setDirty(true);
            setConfirmDefaults(false);
            toast.success(`أُعيدت ${perms.length} صلاحية افتراضية — اضغط «حفظ» للتثبيت`);
          } catch (err) {
            toast.error(apiError(err));
          } finally {
            setLoadingDefaults(false);
          }
        }}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف الدور"
        message={`حذف "${confirmDelete?.name || ''}"؟ سيتم تجريد المستخدمين من هذا الدور.`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteRole.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteRole.mutateAsync(confirmDelete._id || confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null);
              setSelectedId(null);
              toast.success('تم الحذف');
            }
          });
        }}
      />
    </div>
  );
}
